import React from 'react';
import { useLatestRelease } from '../hooks/useLatestRelease';
import { formatBytes } from '../utils/formatBytes';
import { formatDate } from '../utils/formatDate';

/* ── SVG Icons ───────────────────────────────────────────────── */
const IconDevice = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <rect x="5" y="2" width="14" height="20" rx="3" />
    <line x1="12" y1="18" x2="12" y2="18.01" strokeWidth="2.5" />
  </svg>
);

const IconPackage = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
    <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
    <line x1="12" y1="22.08" x2="12" y2="12" />
  </svg>
);

const IconActivity = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <polygon points="5 3 19 12 5 21 5 3" />
  </svg>
);

const IconSettings = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

const IconBroadcast = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="12" r="2" />
    <path d="M16.24 7.76a6 6 0 0 1 0 8.49m-8.48-.01a6 6 0 0 1 0-8.49m11.31-2.82a10 10 0 0 1 0 14.14m-14.14 0a10 10 0 0 1 0-14.14" />
  </svg>
);

const IconList = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <line x1="8" y1="6" x2="21" y2="6" />
    <line x1="8" y1="12" x2="21" y2="12" />
    <line x1="8" y1="18" x2="21" y2="18" />
    <line x1="3" y1="6" x2="3.01" y2="6" strokeWidth="2.5" />
    <line x1="3" y1="12" x2="3.01" y2="12" strokeWidth="2.5" />
    <line x1="3" y1="18" x2="3.01" y2="18" strokeWidth="2.5" />
  </svg>
);

const IconShield = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

const IconSparkles = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 3 9.9 8.2 5 10.3l4.9 2.1L12 17.6l2.1-5.2 4.9-2.1-4.9-2.1L12 3z" />
    <path d="m5 3 .8 1.8L7.6 5.6 5.8 6.4 5 8.2l-.8-1.8L2.4 5.6l1.8-.8L5 3zM19 15l1 2.3 2.3 1-2.3 1L19 22l-1-2.3-2.3-1 2.3-1L19 15z" />
  </svg>
);

const IconApple = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
  </svg>
);

const IconDownload = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

/* ── Data ─────────────────────────────────────────────────────── */
const features = [
  {
    icon: <IconDevice />,
    variant: 'green',
    title: 'Device Inspector',
    desc: 'Battery status, RAM, storage, Android version, and hardware specs — all in one structured panel the moment you connect.',
  },
  {
    icon: <IconPackage />,
    variant: 'blue',
    title: 'Package Manager',
    desc: 'Browse all installed apps, filter by type, search by name, uninstall, clear data — no ADB commands needed.',
  },
  {
    icon: <IconActivity />,
    variant: 'blue',
    title: 'Activity Launcher',
    desc: 'Explore your app\'s Activities grouped by component and launch them directly — perfect for testing deep links and flows.',
  },
  {
    icon: <IconSettings />,
    variant: 'green',
    title: 'Services Browser',
    desc: 'See every running service, inspect details, and start or stop them without ever touching the terminal.',
  },
  {
    icon: <IconBroadcast />,
    variant: 'blue',
    title: 'Broadcast Sender',
    desc: 'Send custom broadcast intents to your device with a visual form. Trigger your receivers without writing shell scripts.',
  },
  {
    icon: <IconList />,
    variant: 'green',
    title: 'System Properties',
    desc: 'Read and search all device system properties at once. Copy values with one click for logs, reports, or debugging.',
  },
  {
    icon: <IconShield />,
    variant: 'blue',
    title: 'Permissions Panel',
    desc: 'Inspect dangerous and runtime permissions quickly. Spot blocked capabilities before they become production bugs.',
  },
  {
    icon: <IconSparkles />,
    variant: 'green',
    title: 'Automation Panel',
    desc: 'Run repeatable device routines from one place. Speed up repetitive validation and setup tasks during QA cycles.',
  },
  {
    icon: <IconSettings />,
    variant: 'blue',
    title: 'Device Settings Browser',
    desc: 'Browse and edit Android system, global, and secure settings from one searchable list — no shell required.',
  },
];

const scenarios = [
  {
    n: '01',
    title: 'Debugging your app between builds?',
    desc: 'Skip the terminal. Clear app data, force-stop, and relaunch in seconds — all from the same window where you work.',
    steps: ['Select your device', 'Open Packages panel', 'Clear data or uninstall in one click'],
  },
  {
    n: '02',
    title: 'Testing deep links and navigation?',
    desc: 'Browse your app\'s Activity tree, pick the exact entry point, and launch it directly. No more adb shell am start guessing games.',
    steps: ['Open Activities panel', 'Search by component name', 'Tap Launch — done'],
  },
  {
    n: '03',
    title: 'Auditing a new device or build?',
    desc: 'Review system state, permissions, and repeatable setup actions from one flow before releasing a build to QA.',
    steps: ['Open Permissions panel', 'Run routine checks in Automation', 'Export or copy any value'],
  },
];

/* ── Component ───────────────────────────────────────────────── */
function Home() {
  const latest = useLatestRelease();

  return (
    <main>
      {/* Hero */}
      <section className="hero">
        <div className="hero-grid-pattern" />
        <div className="hero-glow" />
        <div className="hero-inner">
          <div className="hero-content">
            <div className="hero-tag">
              <span className="hero-tag-dot" />
              macOS Desktop App
            </div>
            <h1 className="hero-title">
              Your Android device,<br />
              <span>under control.</span>
            </h1>
            <p className="hero-subtitle">
              Beep is a macOS app that brings ADB power to a clean, focused interface.
              Connect any Android device and start inspecting, managing, and controlling
              it — without writing a single command.
            </p>
            <div className="hero-actions">
              <a href="/download/latest" className="btn-primary">
                <IconDownload />
                Download for macOS
              </a>
              <a href="#features" className="btn-secondary">
                See features →
              </a>
            </div>
            <p className="hero-note">Free · macOS 12+ · Requires ADB{latest ? ` · v${latest.version}` : ''}</p>
          </div>
          <div className="hero-visual">
            <picture>
              <source type="image/webp" srcSet="/mascot-hero-460.webp 1x, /mascot-hero-920.webp 2x" />
              <img
                src="/mascot-hero-460.webp"
                alt="Beep mascot — a friendly green robot"
                className="hero-mascot"
              />
            </picture>
          </div>
        </div>
      </section>

      {/* Stats */}
      <div className="stats">
        <div className="stats-inner">
          <div>
            <div className="stat-value">9</div>
            <div className="stat-label">Built-in panels</div>
          </div>
          <div>
            <div className="stat-value">0</div>
            <div className="stat-label">Commands to memorize</div>
          </div>
          <div>
            <div className="stat-value">1</div>
            <div className="stat-label">Click to connect</div>
          </div>
          <div>
            <div className="stat-value">∞</div>
            <div className="stat-label">Devices supported</div>
          </div>
        </div>
      </div>

      {/* Features */}
      <section className="section features" id="features">
        <div className="section-inner">
          <div className="features-header">
            <p className="section-eyebrow">Features</p>
            <h2 className="section-title">Everything you need,<br />nothing you don't.</h2>
            <p className="section-subtitle">
              Nine focused panels, each built around a real ADB workflow. No bloat, no configuration. Just connect and go.
            </p>
          </div>
          <div className="feature-theme-showcase">
            <div className="theme-fusion">
              <div className="theme-fusion-light-wrap">
                <img
                  src="/screenshots/packages-light.png"
                  alt="Beep packages panel with a diagonal light and dark theme comparison"
                  className="theme-fusion-img theme-fusion-img-light"
                />
              </div>
              <div className="theme-fusion-dark-wrap" aria-hidden="true">
                <img
                  src="/screenshots/packages-dark.png"
                  alt=""
                  className="theme-fusion-img"
                />
              </div>
              <div className="theme-fusion-divider" aria-hidden="true" />
            </div>
          </div>
          <div className="features-grid">
            {features.map((f) => (
              <div className="feature-card" key={f.title}>
                <div className={`feature-icon ${f.variant}`}>
                  {f.icon}
                </div>
                <h3 className="feature-title">{f.title}</h3>
                <p className="feature-desc">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Scenarios */}
      <section className="section scenarios">
        <div className="section-inner">
          <div className="scenarios-header">
            <p className="section-eyebrow">Real World Use</p>
            <h2 className="section-title">Beep saves the day<br />when you're in the flow.</h2>
            <p className="section-subtitle">
              Built for Android developers who don't want to stop and remember ADB syntax every time they need to do something simple.
            </p>
          </div>
          <div className="scenarios-grid">
            {scenarios.map((s) => (
              <div className="scenario-card" key={s.n}>
                <p className="scenario-number">Scenario {s.n}</p>
                <h3 className="scenario-title">{s.title}</h3>
                <p className="scenario-desc">{s.desc}</p>
                <div className="scenario-steps">
                  {s.steps.map((step, i) => (
                    <div className="scenario-step" key={i}>
                      <span className="scenario-step-dot">{i + 1}</span>
                      {step}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* App Preview */}
      <section className="preview" id="preview">
        <div className="section-inner">
          <div className="preview-header">
            <p className="section-eyebrow">App Preview</p>
            <h2 className="section-title">A clean, structured interface.</h2>
            <p className="section-subtitle">
              Calm surfaces, fast navigation, and clear hierarchy — designed to keep you focused on your device, not the tool.
            </p>
          </div>
          <div className="preview-window">
            <img
              src="/screenshots/device-info-light.png"
              alt="Beep app device info panel with hardware and system details"
              className="preview-img"
            />
          </div>
        </div>
      </section>

      {/* Download */}
      <section className="section download" id="download">
        <div className="download-inner">
          <img
            src="/mascot-icon-120.webp"
            srcSet="/mascot-icon-120.webp 1x, /mascot-icon-240.webp 2x"
            alt="Beep app icon"
            className="download-mascot"
          />
          <h2 className="download-title">Ready to take control?</h2>
          <p className="download-subtitle">
            Download Beep for free and connect your first Android device in under a minute.
            No account, no setup wizard — just open it.
          </p>
          <a href="/download/latest" className="download-btn">
            <IconApple />
            Download for macOS
          </a>
          {latest && (
            <div className="download-version">
              <span className="download-version-tag">v{latest.version}</span>
              <span className="download-version-dot">·</span>
              <span>Released {formatDate(latest.publishedAt)}</span>
              <span className="download-version-dot">·</span>
              <span>{formatBytes(latest.sizeBytes)}</span>
            </div>
          )}
          <div className="download-meta">
            <span>Free</span>
            <span>·</span>
            <span>macOS 12 Monterey or later</span>
            <span>·</span>
            <span>Apple Silicon &amp; Intel</span>
            <span>·</span>
            <span>Requires ADB installed</span>
          </div>
        </div>
      </section>
    </main>
  );
}

export default Home;
